import { useState, useEffect, useCallback } from 'react';
import { useArtifactStore } from '../hooks/useArtifactStore';
import { dataSourceApi } from '../services/api';
import type { DataSourceInfo } from '../services/api';
import type { WidgetProps } from './WidgetRegistry';

interface HealthState {
  status: 'unknown' | 'checking' | 'ok' | 'fail';
  latencyMs?: number;
  error?: string;
}

export function EnvSwitcherWidget({ journalRootId }: WidgetProps) {
  const { activeDataSources, setActiveDataSource, loadActiveDataSources } = useArtifactStore();
  // "Selection applies to this journal only" — load this journal's persisted selection on mount
  // and whenever the journal changes; setActiveDataSource writes through for the same journal.
  useEffect(() => { loadActiveDataSources(journalRootId); }, [journalRootId, loadActiveDataSources]);
  const [sources, setSources] = useState<DataSourceInfo[]>([]);
  const [health, setHealth] = useState<Record<string, HealthState>>({});
  const [error, setError] = useState('');

  const load = useCallback(() => {
    dataSourceApi.list()
      .then(list => { setSources(list); setError(''); })
      .catch(err => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const checkHealth = async (id: string) => {
    setHealth(h => ({ ...h, [id]: { status: 'checking' } }));
    try {
      const result = await dataSourceApi.testConnection(id);
      setHealth(h => ({ ...h, [id]: result.ok
        ? { status: 'ok', latencyMs: result.latencyMs }
        : { status: 'fail', error: result.error } }));
    } catch (err) {
      setHealth(h => ({ ...h, [id]: { status: 'fail', error: err instanceof Error ? err.message : String(err) } }));
    }
  };

  const handleSelect = (product: string, sourceId: string) => {
    const src = sources.find(s => s.id === sourceId);
    if (src?.env === 'prod' && !confirm(`Switch ${product} queries to PRODUCTION (${src.name})? All queries are read-only and audited.`)) {
      return;
    }
    setActiveDataSource(product, sourceId || null);
  };

  const products = [...new Set(sources.map(s => s.product))].sort();

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-2 py-1 border-b border-tower-border/50 shrink-0">
        <div className="text-xs text-tower-muted font-semibold">Environment Switcher</div>
        <div className="text-[10px] text-tower-muted">Selection applies to this journal only</div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-3">
        {error && <div className="text-[11px] text-error">{error}</div>}
        {sources.length === 0 && !error && (
          <div className="text-[11px] text-tower-muted">
            No data sources registered. Seed via POST /api/data-sources/seed.
          </div>
        )}

        {products.map(product => {
          const productSources = sources.filter(s => s.product === product);
          const defaultSource = productSources.find(s => s.isDefault);
          const selected = activeDataSources[product] || '';
          return (
            <div key={product}>
              <div className="text-[10px] text-success font-semibold uppercase tracking-wider mb-1">{product}</div>
              <select
                value={selected}
                onChange={e => handleSelect(product, e.target.value)}
                className="w-full bg-tower-bg text-tower-text text-xs border border-tower-border rounded px-1.5 py-1"
              >
                <option value="">Default ({defaultSource ? `${defaultSource.env} — ${defaultSource.id}` : 'env config'})</option>
                {productSources.map(s => (
                  <option key={s.id} value={s.id} disabled={s.isEnabled === false}>
                    {s.env}{s.env === 'prod' ? ' ⚠' : ''} — {s.id}{s.isEnabled === false ? ' (disabled)' : ''}
                  </option>
                ))}
              </select>

              <div className="mt-1 space-y-0.5">
                {productSources.map(s => {
                  const h = health[s.id] || { status: 'unknown' as const };
                  const isActive = selected === s.id || (!selected && s.isDefault);
                  return (
                    <div key={s.id} className="flex items-center gap-1.5 text-[10px]">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                        h.status === 'ok' ? 'bg-success' :
                        h.status === 'fail' ? 'bg-error' :
                        h.status === 'checking' ? 'bg-warning animate-pulse' : 'bg-tower-border'}`} />
                      <span className={isActive ? 'text-tower-text' : 'text-tower-muted'}>
                        {s.id}{isActive ? ' (active)' : ''}
                      </span>
                      <button
                        onClick={() => checkHealth(s.id)}
                        disabled={h.status === 'checking'}
                        className="ml-auto text-tower-muted hover:text-tower-accent disabled:opacity-50"
                      >
                        {h.status === 'checking' ? '...' : 'check'}
                      </button>
                      {h.status === 'ok' && <span className="text-success">{h.latencyMs}ms</span>}
                      {h.status === 'fail' && <span className="text-error truncate max-w-[100px]" title={h.error}>{(h.error || 'failed').slice(0, 24)}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
